import { z } from 'zod';
import * as R from 'remeda';
import { DurationLikeObject } from '../dates.js';
import { ObjectIdString, OpportunityType, SimpleObjectIdString } from '../common.js';

//--------------------------------------------------------------------------------------------------------------------//
//region Opportunity Type Configs

// Escalation mode options for opportunity visibility/escalation
export const EscalationMode = z.enum([
  'recruiter-then-manager',
  'recruiter-only',
  'manager-only',
  'recruiter-manager-simultaneous',
  'disabled',
]);
export type EscalationMode = z.infer<typeof EscalationMode>;

// Per-opportunity-type timing configuration
export const OpportunityEscalationTiming = z.object({
  escalationMode: EscalationMode,
  detectAfter: DurationLikeObject,
  escalateAfter: DurationLikeObject,
});
export type OpportunityEscalationTiming = z.infer<typeof OpportunityEscalationTiming>;

export const BaseOpportunityConfig = z
  .object({
    escalationTiming: OpportunityEscalationTiming.partial(),
  })
  .partial();

export type BaseOpportunityConfig = z.infer<typeof BaseOpportunityConfig>;

// LossToCompetitor has additional config for company name variants
export const LossToCompetitorConfig = BaseOpportunityConfig.safeExtend({
  companyNameVariants: z
    .string()
    .array()
    .optional()
    .describe('Additional company/brand names to filter out from competitor detection'),
});
export type LossToCompetitorConfig = z.infer<typeof LossToCompetitorConfig>;

export const ExclusionMatchMode = z.enum(['exact', 'contains']);
export type ExclusionMatchMode = z.infer<typeof ExclusionMatchMode>;

export const NoteContentExclusion = z.object({
  content: z.string().trim().array().default([]),
  matchMode: ExclusionMatchMode.default('contains'),
});
export type NoteContentExclusion = z.infer<typeof NoteContentExclusion>;

export const FlowExclusions = z.object({
  noteContent: NoteContentExclusion.default(NoteContentExclusion.parse({})),
  candidateStatuses: z.string().trim().array().default([]),
});
export type FlowExclusions = z.infer<typeof FlowExclusions>;

export const BaseFlowConfig = z.object({
  id: SimpleObjectIdString,
  enabled: z.boolean().default(true),
  startActions: z.string().trim().array().min(1, 'At least one start action required').default([]),
  exclusions: FlowExclusions.default(FlowExclusions.parse({})),
});
export type BaseFlowConfig = z.infer<typeof BaseFlowConfig>;

export const HandoffFlowConfig = z.object({
  ...BaseFlowConfig.shape,
  endActions: z.string().trim().array().min(1, 'At least one end action required').default([]),
  completionWindowDays: z.number().int().positive().default(1),
  maxNotesAfterStartEvent: z.number().int().nullish(),
});
export type HandoffFlowConfig = z.infer<typeof HandoffFlowConfig>;

// Handoff failure has additional flow configuration
export const HandoffFailureConfig = BaseOpportunityConfig.safeExtend({
  flows: HandoffFlowConfig.array().optional(),
});
export type HandoffFailureConfig = z.infer<typeof HandoffFailureConfig>;

export const InboundFollowUpFlowConfig = z.object({
  ...BaseFlowConfig.shape,
  inboundActions: z.string().trim().array().default([]),
  maxNotesBetweenStartActionAndInbound: z.number().int().positive().nullish(),
  excludeIfHasQuestion: z.boolean().default(true),
  ownerSource: z.enum(['noteAuthor', 'candidateOwner']).default('noteAuthor'),
});
export type InboundFollowUpFlowConfig = z.infer<typeof InboundFollowUpFlowConfig>;

export const InboundFollowUpConfig = BaseOpportunityConfig.safeExtend({
  flows: InboundFollowUpFlowConfig.array().optional(),
});
export type InboundFollowUpConfig = z.infer<typeof InboundFollowUpConfig>;

// Placement-linked types can hand ownership to whoever a placement field names (e.g. a service consultant)
export const PlacementOpportunityConfig = BaseOpportunityConfig.safeExtend({
  placementOwnerField: z
    .string()
    .trim()
    .optional()
    .describe(
      'Bullhorn Placement field that identifies the opportunity owner: a CorporateUser reference (owner) or a name (customText13)'
    ),
});
export type PlacementOpportunityConfig = z.infer<typeof PlacementOpportunityConfig>;

export const placementOpportunityTypes = [
  'placementBackOutRisk',
  'placementReportingFrustration',
] as const satisfies readonly OpportunityType[];
export type PlacementOpportunityType = (typeof placementOpportunityTypes)[number];
export const isPlacementOpportunityType = R.isIncludedIn(placementOpportunityTypes);

export const OPPORTUNITY_TYPE_CONFIG_SCHEMAS = {
  followUpFailure: BaseOpportunityConfig,
  leadAbandonment: BaseOpportunityConfig,
  phoneScreenFollowUp: BaseOpportunityConfig,
  workersCompLegalRisk: BaseOpportunityConfig,
  responseTimeDecline: BaseOpportunityConfig,
  responseTimeImprovement: BaseOpportunityConfig,
  lossToCompetitor: LossToCompetitorConfig,
  placementBackOutRisk: PlacementOpportunityConfig,
  placementReportingFrustration: PlacementOpportunityConfig,
  handoffFailure: HandoffFailureConfig,
  inboundFollowUp: InboundFollowUpConfig,
} as const;

export const OpportunityTypeConfigs = z.object(OPPORTUNITY_TYPE_CONFIG_SCHEMAS);
export type OpportunityTypeConfigs = z.infer<typeof OpportunityTypeConfigs>;

export const StaffIQConfig = z.object({
  enabled: z.boolean().default(false),
  opportunityTypeConfigs: OpportunityTypeConfigs.partial().optional(),
  // When enabled, users only see opportunities for recruiters in their department(s)
  restrictToUserDepartment: z.boolean().default(false),
  // When enabled, newly discovered recruiters will automatically have emailAlertsEnabled set to true
  autoEnrollOwnersInEmailAlerts: z.boolean().default(false),
  disableRevalidationAndAutoResolve: z.boolean().default(false),
});
export type StaffIQConfig = z.infer<typeof StaffIQConfig>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Config update API schemas

export const HandoffFailureConfigUpdate = HandoffFailureConfig.omit({ flows: true }).extend({
  flowUpdates: z
    .object({
      added: HandoffFlowConfig.array().optional(),
      removed: ObjectIdString.array().optional(),
      modified: HandoffFlowConfig.array().optional(),
    })
    .nullish(),
});
export type HandoffFailureConfigUpdate = z.infer<typeof HandoffFailureConfigUpdate>;

export const InboundFollowUpConfigUpdate = InboundFollowUpConfig.omit({ flows: true }).extend({
  flowUpdates: z
    .object({
      added: InboundFollowUpFlowConfig.array().optional(),
      removed: ObjectIdString.array().optional(),
      modified: InboundFollowUpFlowConfig.array().optional(),
    })
    .nullish(),
});
export type InboundFollowUpConfigUpdate = z.infer<typeof InboundFollowUpConfigUpdate>;

export const OpportunityTypeConfigsUpdate = OpportunityTypeConfigs.extend({
  handoffFailure: HandoffFailureConfigUpdate,
  inboundFollowUp: InboundFollowUpConfigUpdate,
});
export type OpportunityTypeConfigsUpdate = z.infer<typeof OpportunityTypeConfigsUpdate>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
